/**
 * Workspace-scoped HTTP fetcher used by `ContactsClient` and any future
 * typed client over the `/api/workspaces/:wsid/...` surface.
 *
 * Per FD `aidlc-docs/construction/contacts/functional-design/pr-c-dashboard.md § 3.1`,
 * with deltas from the auth-redesign (cookies removed):
 *
 *  - Auth is `Authorization: Bearer <jwt>` from {@link tokenStore} (i.e.,
 *    the SPA's `localStorage`). Same transport as
 *    `apps/dashboard/src/agents/api/client.ts` and the shared
 *    `AuthClient`. Cookies + CSRF are gone — no `credentials: 'include'`,
 *    no `X-CSRF-Token` header.
 *  - Workspace identity in URLs is the UUID (`:wsid`), NOT the slug —
 *    `WsidWorkspaceGuard` (`packages/auth/src/server/.../wsid-workspace.guard.ts`)
 *    enforces this. The slug only lives in dashboard URLs.
 *  - On 401: clear the stored token (it's invalid) so the SPA's auth
 *    state on the next render reflects the server's view; let the error
 *    propagate.
 *
 * Non-2xx responses parse the flat error envelope and throw `ApiError`.
 */

import { clearToken, getToken } from "@cometchat-ai/auth/client";

import { ApiError, parseApiError } from "./errors";

export interface WorkspaceFetcher {
  get<T>(path: string, query?: Record<string, string | number | undefined>): Promise<T>;
  post<T>(path: string, body?: unknown): Promise<T>;
  patch<T>(path: string, body: unknown): Promise<T>;
  delete<T>(path: string): Promise<T>;
}

export interface WorkspaceFetcherOptions {
  /** Workspace UUID — bound at construction; never re-passed per call. */
  workspaceId: string;
  /** API base URL, e.g. `http://localhost:3000`. No trailing slash. */
  apiBaseUrl: string;
  /** Override fetch (tests). */
  fetchImpl?: typeof fetch;
  /**
   * Override token getter (tests). Defaults to {@link tokenStore.get}
   * (reads from `localStorage`).
   */
  getToken?: () => string | null;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

export function makeWorkspaceFetcher(opts: WorkspaceFetcherOptions): WorkspaceFetcher {
  const baseUrl = opts.apiBaseUrl.replace(/\/$/, "");
  const fetchImpl =
    opts.fetchImpl ?? ((input: RequestInfo | URL, init?: RequestInit) => fetch(input, init));
  const readToken = opts.getToken ?? getToken;
  const prefix = `/api/workspaces/${opts.workspaceId}`;

  async function request<T>(
    method: string,
    path: string,
    body?: unknown,
    query?: Record<string, string | number | undefined>,
  ): Promise<T> {
    const headers: Record<string, string> = { Accept: "application/json" };
    if (body !== undefined) headers["Content-Type"] = "application/json";
    const token = readToken();
    if (token) headers["Authorization"] = `Bearer ${token}`;

    let url = `${baseUrl}${prefix}${path}`;
    if (query) {
      const params = new URLSearchParams();
      for (const [k, v] of Object.entries(query)) {
        if (v !== undefined && v !== "") params.set(k, String(v));
      }
      const qs = params.toString();
      if (qs) url += `?${qs}`;
    }

    const res = await fetchImpl(url, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    if (res.status === 204) return undefined as T;
    const text = await res.text();
    const parsed = text.length > 0 ? (safeParse(text) as unknown) : undefined;

    if (!res.ok) {
      if (res.status === 401) clearToken();
      throw parseApiError(res.status, parsed, res.statusText);
    }
    return parsed as T;
  }

  return {
    get: <T>(path: string, query?: Record<string, string | number | undefined>) =>
      request<T>("GET", path, undefined, query),
    post: <T>(path: string, body?: unknown) => request<T>("POST", path, body),
    patch: <T>(path: string, body: unknown) => request<T>("PATCH", path, body),
    delete: <T>(path: string) => request<T>("DELETE", path),
  };
}

export { ApiError };
